'use client'

import { useCallback, useMemo, type MutableRefObject } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { markdown, markdownLanguage } from '@codemirror/lang-markdown'
import { languages } from '@codemirror/language-data'
import {
  EditorView,
  Decoration,
  type DecorationSet,
  ViewPlugin,
  type ViewUpdate,
  keymap,
} from '@codemirror/view'
import { syntaxTree } from '@codemirror/language'
import { RangeSetBuilder } from '@codemirror/state'
import { htmlToMarkdown } from '@/lib/html-to-markdown'

// Decoration marks for subtle syntax markers
const subtleMark = Decoration.mark({ class: 'cm-md-subtle' })
const boldMark = Decoration.mark({ class: 'cm-md-bold' })
const italicMark = Decoration.mark({ class: 'cm-md-italic' })
const headingMark = Decoration.mark({ class: 'cm-md-heading' })
const heading1Mark = Decoration.mark({ class: 'cm-md-heading1' })
const heading2Mark = Decoration.mark({ class: 'cm-md-heading2' })
const heading3Mark = Decoration.mark({ class: 'cm-md-heading3' })
const linkMark = Decoration.mark({ class: 'cm-md-link' })
const codeMark = Decoration.mark({ class: 'cm-md-code' })
const blockquoteMark = Decoration.mark({ class: 'cm-md-blockquote' })
const listMarkerMark = Decoration.mark({
  class: 'cm-md-list-marker',
})
const hrMark = Decoration.mark({ class: 'cm-md-hr' })

function buildDecorations(view: EditorView): DecorationSet {
  try {
    const builder = new RangeSetBuilder<Decoration>()
    const tree = syntaxTree(view.state)

    tree.iterate({
      enter(node) {
        const { from, to } = node
        const type = node.type.name

        // Bold: StrongEmphasis contains EmphasisMark tokens
        if (type === 'StrongEmphasis') {
          builder.add(from, to, boldMark)
          builder.add(from, from + 2, subtleMark)
          builder.add(to - 2, to, subtleMark)
        }

        // Italic: Emphasis
        if (type === 'Emphasis') {
          builder.add(from, to, italicMark)
          builder.add(from, from + 1, subtleMark)
          builder.add(to - 1, to, subtleMark)
        }

        // Headings
        if (type.startsWith('ATXHeading')) {
          builder.add(from, to, headingMark)
          const level = type.replace('ATXHeading', '')
          if (level === '1') builder.add(from, to, heading1Mark)
          if (level === '2') builder.add(from, to, heading2Mark)
          if (level === '3') builder.add(from, to, heading3Mark)
        }

        if (type === 'HeaderMark') {
          builder.add(from, to, subtleMark)
        }

        // Links
        if (type === 'Link') builder.add(from, to, linkMark)
        if (type === 'URL') builder.add(from, to, subtleMark)
        if (type === 'LinkMark') builder.add(from, to, subtleMark)

        // Code
        if (type === 'InlineCode') builder.add(from, to, codeMark)
        if (type === 'CodeMark') builder.add(from, to, subtleMark)
        if (type === 'FencedCode') builder.add(from, to, codeMark)

        // Blockquote
        if (type === 'Blockquote') builder.add(from, to, blockquoteMark)
        if (type === 'QuoteMark') builder.add(from, to, subtleMark)

        // List markers
        if (type === 'ListMark') builder.add(from, to, listMarkerMark)

        // Horizontal rule
        if (type === 'HorizontalRule') builder.add(from, to, hrMark)
      },
    })

    return builder.finish()
  } catch {
    // Graceful degradation — see sub-issue R carve-out
    // CodeMirror parse-tree walker may throw on transient parse states; runs
    // per-keystroke via update(), so reporting would create a high-volume noise channel.
    return Decoration.none
  }
}

// Plugin that rebuilds decorations on every change
const markdownDecorations = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet
    constructor(view: EditorView) {
      this.decorations = buildDecorations(view)
    }
    update(update: ViewUpdate) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = buildDecorations(update.view)
      }
    }
  },
  {
    decorations: v => v.decorations,
  },
)

// Base theme for iA Writer-style inline formatting
const markdownTheme = EditorView.baseTheme({
  '&': {
    // Pinned at 1rem to prevent iOS Safari focus-zoom (#7918)
    fontSize: '1rem',
    fontFamily: 'inherit',
    backgroundColor: 'transparent !important',
  },
  '&.cm-editor': {
    backgroundColor: 'transparent !important',
  },
  '.cm-content': {
    fontFamily: 'inherit',
    padding: '8px 0',
  },
  '.cm-line': {
    padding: '0 4px',
  },
  // Cursor color is set in lightTheme/darkTheme with EditorView.theme()
  // for higher specificity than CodeMirror's default black cursor.
  '.cm-focused': {
    outline: 'none',
  },
  '.cm-scroller': {
    overflow: 'auto',
    height: '100%',
    // #9726 — CodeMirror's default theme sets monospace on `.cm-scroller`,
    // which `.cm-content { fontFamily: inherit }` then picks up. Override here
    // so the editor uses the app's normal UI font everywhere (inline code keeps
    // its own monospace via `.cm-md-code`).
    fontFamily: 'inherit',
  },
  // Subtle syntax markers
  '.cm-md-subtle': {
    opacity: '0.35',
    fontSize: '0.85em',
  },
  // Bold text
  '.cm-md-bold': {
    fontWeight: '700',
  },
  // Italic text
  '.cm-md-italic': {
    fontStyle: 'italic',
  },
  // Headings
  '.cm-md-heading': {
    fontWeight: '700',
  },
  '.cm-md-heading1': {
    fontSize: '1.5em',
    lineHeight: '1.3',
  },
  '.cm-md-heading2': {
    fontSize: '1.25em',
    lineHeight: '1.3',
  },
  '.cm-md-heading3': {
    fontSize: '1.1em',
    lineHeight: '1.3',
  },
  // Links
  '.cm-md-link': {
    color: 'var(--md-link-color, #2563eb)',
    textDecoration: 'underline',
    textDecorationColor: 'var(--md-link-color, #2563eb)',
    textUnderlineOffset: '2px',
  },
  // Inline code
  '.cm-md-code': {
    backgroundColor: 'var(--md-code-bg, rgba(0, 0, 0, 0.06))',
    borderRadius: '3px',
    padding: '1px 4px',
    fontSize: '0.9em',
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
  },
  // Blockquote
  '.cm-md-blockquote': {
    borderLeft: '3px solid var(--md-blockquote-border, #cbd5e1)',
    paddingLeft: '12px',
    color: 'var(--md-blockquote-color, #64748b)',
  },
  // List markers
  '.cm-md-list-marker': {
    color: 'var(--md-list-color, var(--color-text-secondary))',
    fontWeight: '600',
  },
  // Horizontal rule
  '.cm-md-hr': {
    color: 'var(--md-hr-color, #cbd5e1)',
    textAlign: 'center',
  },
})

// Light/dark theme variables use CSS custom properties consumed by baseTheme.
// `as any` is needed because CodeMirror's theme type doesn't include custom properties.
const lightTheme = EditorView.theme({
  '&': {
    '--md-link-color': '#2563eb',
    '--md-code-bg': 'rgba(0, 0, 0, 0.06)',
    '--md-blockquote-border': '#cbd5e1',
    '--md-blockquote-color': '#64748b',
    // Marker glyphs are text: the slate-400/500 literals failed AA the same
    // way the placeholder did (#15137).
    '--md-list-color': 'var(--color-text-secondary)',
    '--md-hr-color': '#cbd5e1',
    color: '#0f172a',
    backgroundColor: 'transparent',
  } as Record<string, string>,
  '.cm-cursor, .cm-dropCursor': {
    borderLeftColor: '#3b82f6',
  },
})

const darkTheme = EditorView.theme({
  '&': {
    '--md-link-color': '#60a5fa',
    '--md-code-bg': 'rgba(255, 255, 255, 0.1)',
    '--md-blockquote-border': '#475569',
    '--md-blockquote-color': '#94a3b8',
    '--md-list-color': 'var(--color-text-secondary)',
    '--md-hr-color': '#475569',
    color: '#f1f5f9',
    backgroundColor: 'transparent',
  } as Record<string, string>,
  '.cm-cursor, .cm-dropCursor': {
    borderLeftColor: '#60a5fa',
  },
})

export interface MarkdownEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  isDark?: boolean
  minHeight?: string
  maxHeight?: string
  onFocus?: () => void
  onBlur?: () => void
  /** Keyboard shortcut handlers from parent */
  onKeyDown?: (event: KeyboardEvent) => boolean
  /** Ref to access the CodeMirror EditorView for cursor-aware operations */
  editorViewRef?: MutableRefObject<EditorView | null>
  /** Maximum character count — paste handler truncates converted markdown to fit */
  maxLength?: number
  /**
   * Accessible name applied to CodeMirror's contenteditable region via
   * `EditorView.contentAttributes` as `aria-label`.
   */
  ariaLabel?: string
}

export function MarkdownEditor({
  value,
  onChange,
  placeholder = '',
  disabled = false,
  isDark = false,
  minHeight = '120px',
  maxHeight,
  onFocus,
  onBlur,
  onKeyDown,
  editorViewRef,
  maxLength,
  ariaLabel,
}: MarkdownEditorProps) {
  const handleChange = useCallback(
    (val: string) => {
      onChange(val)
    },
    [onChange],
  )

  const extensions = useMemo(() => {
    const exts = [
      markdown({
        base: markdownLanguage,
        codeLanguages: languages,
      }),
      markdownDecorations,
      markdownTheme,
      isDark ? darkTheme : lightTheme,
      EditorView.lineWrapping,
      EditorView.contentAttributes.of({
        spellcheck: 'true',
        autocorrect: 'on',
        autocapitalize: 'on',
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      }),
      EditorView.theme({
        '&': {
          height: '100%',
        },
        '.cm-scroller': {
          minHeight,
          ...(maxHeight ? { maxHeight } : {}),
        },
      }),
    ]

    if (placeholder) {
      exts.push(
        EditorView.theme({
          '.cm-placeholder': {
            // The semantic token carries both themes and meets AA on
            // surface-default in each; the old slate-400/500 literals were
            // 2.56:1 and 3.75:1 (#15137).
            color: 'var(--color-text-secondary)',
            fontStyle: 'normal',
          },
        }),
      )
    }

    if (onKeyDown) {
      exts.push(
        keymap.of([
          {
            key: 'Mod-b',
            run: view => {
              return onKeyDown(
                new KeyboardEvent('keydown', {
                  key: 'b',
                  metaKey: true,
                  ctrlKey: false,
                }),
              )
            },
          },
          {
            key: 'Mod-i',
            run: view => {
              return onKeyDown(
                new KeyboardEvent('keydown', {
                  key: 'i',
                  metaKey: true,
                  ctrlKey: false,
                }),
              )
            },
          },
        ]),
      )
    }

    exts.push(
      EditorView.domEventHandlers({
        paste(e, view) {
          const html = e.clipboardData?.getData('text/html') ?? ''
          if (!html) return
          let convertedMarkdown = htmlToMarkdown(html)
          if (!convertedMarkdown) return
          const { from, to } = view.state.selection.main
          if (maxLength !== undefined) {
            const remaining = maxLength - (view.state.doc.length - (to - from))
            if (remaining <= 0) {
              e.preventDefault()
              return
            }
            if (convertedMarkdown.length > remaining) {
              // Truncate at the last whitespace boundary within the budget so
              // we never split a markdown token (e.g. **hello wo remains unbalanced).
              // If no safe boundary exists, fall through to native plain-text paste
              // instead of committing corrupted markdown.
              const safeSlice = convertedMarkdown.slice(0, remaining)
              const lastBoundary = Math.max(
                safeSlice.lastIndexOf('\n'),
                safeSlice.lastIndexOf(' '),
              )
              if (lastBoundary <= 0) return // fall through to native paste
              convertedMarkdown = safeSlice.slice(0, lastBoundary).trimEnd()
            }
          }
          view.dispatch({
            changes: { from, to, insert: convertedMarkdown },
            userEvent: 'input.paste',
          })
          e.preventDefault()
          // onChange is fired by @uiw/react-codemirror's updateListener for
          // any local docChanged transaction — no manual call needed here.
        },
      }),
    )

    return exts
  }, [
    isDark,
    minHeight,
    maxHeight,
    placeholder,
    onKeyDown,
    maxLength,
    onChange,
    ariaLabel,
  ])

  return (
    <div data-testid="markdown-editor" className="markdown-editor h-full">
      <CodeMirror
        value={value}
        onChange={handleChange}
        extensions={extensions}
        placeholder={placeholder}
        editable={!disabled}
        indentWithTab={false}
        basicSetup={{
          lineNumbers: false,
          foldGutter: false,
          highlightActiveLine: false,
          highlightSelectionMatches: false,
          indentOnInput: true,
          bracketMatching: false,
          closeBrackets: false,
          autocompletion: false,
          crosshairCursor: false,
          rectangularSelection: false,
          highlightActiveLineGutter: false,
          dropCursor: true,
          allowMultipleSelections: false,
          history: true,
          drawSelection: true,
          syntaxHighlighting: false,
        }}
        onFocus={onFocus}
        onBlur={onBlur}
        onCreateEditor={view => {
          if (editorViewRef) editorViewRef.current = view
        }}
      />
    </div>
  )
}
